'use client';

import React from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import Link from 'next/link';

const coreValues = [
  {
    title: 'Transparency',
    description: 'We believe every borrower deserves clear, honest information about their loan options, rates, and terms — no hidden fees, no fine-print surprises.',
    icon: '◈',
  },
  {
    title: 'Integrity',
    description: 'Our recommendations are driven solely by what is best for you. We never push products that do not align with your financial situation.',
    icon: '◆',
  },
  {
    title: 'Accessibility',
    description: 'Quality lending guidance should not be reserved for the few. We work to connect every applicant — regardless of background — with the right program.',
    icon: '◉',
  },
  {
    title: 'Accountability',
    description: 'We stand behind every interaction. Our team is reachable, responsive, and committed to resolving concerns quickly and professionally.',
    icon: '◎',
  },
  {
    title: 'Compliance',
    description: 'We operate within all applicable federal and state lending regulations, ensuring every process meets the highest legal and ethical standards.',
    icon: '◐',
  },
  {
    title: 'Empowerment',
    description: 'We equip borrowers with the knowledge and tools to make confident, informed financial decisions — not just today, but for the long term.',
    icon: '◑',
  },
];

const whyChoose = [
  { stat: '15+', label: 'Years of Industry Experience' },
  { stat: '50K+', label: 'Borrowers Assisted' },
  { stat: '200+', label: 'Lending Programs Available' },
  { stat: '48hr', label: 'Average Decision Timeline' },
];

export default function AboutPage() {
  return (
    <main className="min-h-screen bg-cloud">
      <Header />
      {/* Hero */}
      <section className="pt-28 pb-16 px-6 lg:px-10 bg-white border-b border-slate-mist/30">
        <div className="max-w-5xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-sky-light text-sky-dark text-xs font-semibold font-body mb-6 tracking-wide uppercase">
            About Lenders Home LLC
          </div>
          <h1 className="font-heading text-display font-bold text-slate-dark leading-tight mb-6">
            Connecting Borrowers with the Right Lending Solutions
          </h1>
          <p className="text-lg text-slate-fog font-body leading-relaxed max-w-3xl">
            Lenders Home LLC is a lending services platform dedicated to helping individuals, families, and businesses access the financial resources they need. We operate with a singular focus: matching qualified borrowers with appropriate lending programs through a transparent, compliant, and customer-first process.
          </p>
        </div>
      </section>
      {/* Company Overview */}
      <section className="py-16 px-6 lg:px-10">
        <div className="max-w-5xl mx-auto grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <h2 className="font-heading text-2xl font-bold text-slate-dark mb-4">Company Overview</h2>
            <p className="text-slate-fog font-body leading-relaxed mb-4">
              Founded with the mission of making responsible lending accessible to all, Lenders Home LLC serves as a bridge between borrowers and a curated network of lending programs. We are not a direct lender — we are a trusted intermediary that evaluates your financial profile and connects you with programs best suited to your needs.
            </p>
            <p className="text-slate-fog font-body leading-relaxed mb-4">
              Our platform is built on decades of combined experience in mortgage lending, personal finance, business credit, and healthcare financing. We understand that every borrower's situation is unique, and we approach each application with the care and diligence it deserves.
            </p>
            <p className="text-slate-fog font-body leading-relaxed">
              Lenders Home LLC has helped tens of thousands of borrowers navigate the lending landscape — from first-time homebuyers to established business owners seeking growth capital.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {whyChoose?.map((item) => (
              <div key={item?.label} className="bg-white rounded-2xl p-6 shadow-card border border-slate-mist/30 text-center">
                <div className="font-heading text-3xl font-bold text-sky mb-1">{item?.stat}</div>
                <div className="text-sm text-slate-fog font-body leading-snug">{item?.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Mission & Vision */}
      <section className="py-16 px-6 lg:px-10 bg-white border-y border-slate-mist/30">
        <div className="max-w-5xl mx-auto grid lg:grid-cols-2 gap-10">
          <div className="bg-sky-light rounded-3xl p-8 border border-sky/20">
            <div className="text-sky text-2xl mb-3">◈</div>
            <h2 className="font-heading text-xl font-bold text-slate-dark mb-3">Our Mission</h2>
            <p className="text-slate-fog font-body leading-relaxed">
              To provide every borrower — regardless of financial background — with transparent access to responsible lending solutions, delivered through a compliant, technology-driven process that prioritizes their long-term financial well-being.
            </p>
          </div>
          <div className="bg-sage-light rounded-3xl p-8 border border-sage/20">
            <div className="text-sage-dark text-2xl mb-3">◆</div>
            <h2 className="font-heading text-xl font-bold text-slate-dark mb-3">Our Vision</h2>
            <p className="text-slate-fog font-body leading-relaxed">
              To become the most trusted lending services platform in the United States — recognized for our integrity, our compliance standards, and our unwavering commitment to helping borrowers achieve their financial goals through informed, responsible borrowing.
            </p>
          </div>
        </div>
      </section>
      {/* Core Values */}
      <section className="py-16 px-6 lg:px-10">
        <div className="max-w-5xl mx-auto">
          <div className="mb-10">
            <h2 className="font-heading text-2xl font-bold text-slate-dark mb-3">Core Values</h2>
            <p className="text-slate-fog font-body">The principles that guide every decision we make at Lenders Home LLC.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {coreValues?.map((v) => (
              <div key={v?.title} className="bg-white rounded-2xl p-6 shadow-card border border-slate-mist/30 hover:shadow-card-hover transition-shadow duration-300">
                <div className="text-sky text-xl mb-3">{v?.icon}</div>
                <h3 className="font-heading text-base font-bold text-slate-dark mb-2">{v?.title}</h3>
                <p className="text-sm text-slate-fog font-body leading-relaxed">{v?.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Responsible Lending */}
      <section className="py-16 px-6 lg:px-10 bg-white border-y border-slate-mist/30">
        <div className="max-w-5xl mx-auto grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <h2 className="font-heading text-2xl font-bold text-slate-dark mb-4">Commitment to Responsible Lending</h2>
            <p className="text-slate-fog font-body leading-relaxed mb-4">
              Responsible lending is not a checkbox — it is the foundation of everything we do. Lenders Home LLC is committed to ensuring that every borrower we assist is matched with a program that is genuinely appropriate for their financial circumstances.
            </p>
            <p className="text-slate-fog font-body leading-relaxed mb-4">
              We conduct thorough eligibility assessments before presenting any lending options. We do not encourage borrowers to take on debt they cannot reasonably service. Our advisors are trained to identify and communicate risk factors clearly, so borrowers can make fully informed decisions.
            </p>
            <p className="text-slate-fog font-body leading-relaxed">
              We adhere to all applicable federal and state lending regulations, including the Truth in Lending Act (TILA), the Equal Credit Opportunity Act (ECOA), and the Fair Housing Act (FHA), among others.
            </p>
          </div>
          <div>
            <h2 className="font-heading text-2xl font-bold text-slate-dark mb-4">Customer-First Approach</h2>
            <p className="text-slate-fog font-body leading-relaxed mb-4">
              At Lenders Home LLC, the borrower is always at the center of every decision. From the moment you submit your application to the day your loan is funded, our team is dedicated to providing a seamless, supportive experience.
            </p>
            <ul className="space-y-3">
              {[
                'Dedicated loan advisors available throughout the process',
                'Real-time application status updates via your dashboard',
                'Plain-language explanations of all terms and conditions',
                'No pressure tactics — you decide if and when to proceed',
                'Post-funding support for repayment questions and concerns',
              ]?.map((item) => (
                <li key={item} className="flex items-start gap-3 text-sm text-slate-fog font-body">
                  <span className="text-sky mt-0.5 flex-shrink-0">✓</span>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
      {/* CTA */}
      <section className="py-16 px-6 lg:px-10">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-heading text-2xl font-bold text-slate-dark mb-4">Ready to See What You Qualify For?</h2>
          <p className="text-slate-fog font-body mb-8">Start your application today and let our team match you with the right lending program for your needs.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link href="/" className="px-8 py-3 bg-sky text-white rounded-full font-semibold font-body hover:bg-sky-dark transition-colors shadow-sky/30 shadow-md">
              Get Started
            </Link>
            <Link href="/contact" className="px-8 py-3 bg-white text-slate border border-slate-mist rounded-full font-semibold font-body hover:bg-cloud transition-colors">
              Contact Us
            </Link>
          </div>
        </div>
      </section>
      <Footer />
    </main>
  );
}
